import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleField, UserSelectField, LabelWithHelp, SecretPasswordField } from '../../../components';
import { Field } from 'formik';

interface MetabaseFormProps {};

function MetabaseForm(props: MetabaseFormProps) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 space-x-0 lg:space-x-12">
            <div className="flex-1">
                <h2 className="mt-4 mb-1 text-base font-semibold leadin text-lg text-gray-900 dark:text-gray-100">
                    {t('settings.metabase')}
                </h2>
                <div className="mt-5">
                    <div className="mt-2 flex space-between">
                        <label htmlFor="enable" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.enable_metabase')}
                        </label>
                        <ToggleField name="metabase.enabled" className="inline"/>
                    </div>
                </div>
                <div className="mt-5">
                    <div className="mt-1 flex flex-col lg:mt-0 lg:flex-row lg:flex-wrap lg:space-x-6">
                        <div className="flex-1 w-100">
                            <label htmlFor="host" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                {t('settings.metabase_host')}
                            </label>
                            <div className="mt-2">
                                <Field
                                    type="text"
                                    name="metabase.host"
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                    placeholder="https://metabase.io"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <h3 className="mt-5 mb-1 text-base font-semibold leadin text-md text-gray-900 dark:text-gray-100">
                    {t('settings.metabase_static_embed.title')}
                </h3>
                <div className="mt-2">
                    <label htmlFor="secret_key" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.metabase_static_embed.secret_key')}
                    </label>
                    <small>{t('settings.metabase_static_embed.secret_key_desc')}</small>
                    <div className="mt-2">
                        <SecretPasswordField
                            name="metabase.static_embed.secret_key"
                            hasFlagName="metabase.static_embed.hasSecretKey"
                        />
                    </div>
                </div>
                <div className="mt-2 flex gap-2">
                    <div className="flex-1">
                        <LabelWithHelp htmlFor="general_dashboard_id" help={t('settings.help.metabase') || ''}>
                            {t('settings.metabase_static_embed.general_dashboard_id')}
                        </LabelWithHelp>
                        <small>{t('settings.metabase_static_embed.project_dashboard_id_desc')}</small>
                        <div className="mt-2">
                            <Field
                                type="number"
                                name="metabase.static_embed.general_dashboard_id"
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                placeholder="42"
                            />
                        </div>
                    </div>
                    <div className="flex-1">
                        <LabelWithHelp htmlFor="project_dashboard_id" help={t('settings.help.metabase') || ''}>
                            {t('settings.metabase_static_embed.project_dashboard_id')}
                        </LabelWithHelp>
                        <small>{t('settings.metabase_static_embed.project_dashboard_id_desc')}</small>
                        <div className="mt-2">
                            <Field
                                type="number"
                                name="metabase.static_embed.project_dashboard_id"
                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                placeholder="42"
                            />
                        </div>
                    </div>
                </div>
                <h3 className="mt-4 mb-1 text-base font-semibold leadin text-sm text-gray-900 dark:text-gray-100">
                    {t('settings.metabase_static_embed.theme.title')}
                </h3>
                <div className="mt-0">
                    <div className="mt-2 flex space-between">
                        <label htmlFor="dark" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.metabase_static_embed.theme.dark')}
                        </label>
                        <ToggleField name="metabase.static_embed.theme.dark" className="inline"/>
                    </div>
                    <div className="mt-2 flex space-between">
                        <label htmlFor="transparent_bg" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.metabase_static_embed.theme.transparent_bg')}
                        </label>
                        <ToggleField name="metabase.static_embed.theme.transparent_bg" className="inline"/>
                    </div>
                    <div className="mt-2 flex space-between">
                        <label htmlFor="bordered" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.metabase_static_embed.theme.bordered')}
                        </label>
                        <ToggleField name="metabase.static_embed.theme.bordered" className="inline"/>
                    </div>
                    <div className="mt-2 flex space-between">
                        <label htmlFor="titled" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.metabase_static_embed.theme.titled')}
                        </label>
                        <ToggleField name="metabase.static_embed.theme.titled" className="inline"/>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MetabaseForm;
